import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config.js';

const LiveSlideCard = ({ sessionId, slide, pipelineStatus }) => {
    const isFinished = ["ASSEMBLING", "DONE"].includes(pipelineStatus);
    const [rawCode, setRawCode] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [viewMode, setViewMode] = useState("preview");
    const [isStreamDone, setIsStreamDone] = useState(isFinished);
    const [copied, setCopied] = useState(false);

    const isTextReady = slide.status === "VERIFIED" || slide.status === "PREVIEW_READY" || slide.status === "APPROVED";

    useEffect(() => {
        const fetchCachedDesign = async () => {
            if (isTextReady && (isFinished || slide.status === "PREVIEW_READY" || slide.status === "APPROVED")) {
                try {
                    const response = await fetch(`${API_BASE_URL}/api/v1/stream_slide/${sessionId}/${slide.slide_id}`);
                    if (response.ok) {
                        const text = await response.text();
                        if (text.includes("data: ")) {
                            const cleanHtml = text
                                .split("\n")
                                .filter(line => line.startsWith("data: "))
                                .map(line => {
                                    try {
                                        return JSON.parse(line.replace("data: ", "")).token;
                                    } catch {
                                        return "";
                                    }
                                })
                                .join("");
                            setRawCode(cleanHtml);
                        } else {
                            setRawCode(text);
                        }
                        setIsStreamDone(true);
                        setIsGenerating(false);
                    }
                } catch (err) {
                    console.error("Error restoring cached HTML layout:", err);
                }
            }
        };

        fetchCachedDesign();
    }, [sessionId, slide.slide_id, isTextReady, isFinished, slide.status]);

    useEffect(() => {
        if (slide.status === "PENDING" || slide.status === "WRITING") {
            setRawCode("");
            setIsStreamDone(false);
            setIsGenerating(false);
        }
    }, [slide.status]);

    useEffect(() => {
        if (!isTextReady || isStreamDone || isFinished) return;

        let eventSource;
        let retryCount = 0;
        const maxRetries = 3;

        const connectStream = () => {
            setIsGenerating(true);
            eventSource = new EventSource(`${API_BASE_URL}/api/v1/stream_slide/${sessionId}/${slide.slide_id}`);

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.token) {
                    setRawCode((prev) => prev + data.token);
                }
            };

            eventSource.onerror = (err) => {
                eventSource.close();

                if (retryCount < maxRetries && !isStreamDone) {
                    retryCount++;
                    setTimeout(connectStream, 2000 * retryCount);
                } else {
                    setIsGenerating(false);
                    setIsStreamDone(true);
                }
            };
        };

        connectStream();

        return () => {
            if (eventSource) eventSource.close();
        };
    }, [sessionId, slide.slide_id, isTextReady, isStreamDone, isFinished]);

    // Tokenizer utility for professional code highlighting
    const highlightSyntax = (code) => {
        if (!code) return "";

        let escaped = code
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

        // Highlights HTML Comments: escaped = escaped.replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span style="color: #6a737d; font-style: italic;">$1</span>');

        // Highlights Attributes: class="..." id="..." style="..."
        escaped = escaped.replace(/(\s[a-zA-Z0-9_-]+)(=)("[^"]*")/g, (match, attr, eq, val) => {
            return ` <span style="color: #9cdcfe;">${attr}</span>${eq}<span style="color: #ce9178;">${val}</span>`;
        });

        // Highlights Tags: &lt;div &lt;/div&gt;
        escaped = escaped.replace(/(&lt;\/?[a-zA-Z0-9:-]+)/g, '<span style="color: #569cd6;">$1</span>');
        escaped = escaped.replace(/(\/?&gt;)/g, '<span style="color: #569cd6;">$1</span>');

        return escaped;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(rawCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isTextReady) {
        return (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", background: "white", marginBottom: "20px", padding: "50px 40px", textAlign: "center", color: "#64748b", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
                <style>
                    {`
                        .loader-container { 
                            display: flex; 
                            justify-content: center; 
                            align-items: center; 
                            margin-bottom: 40px; 
                            /* perspective helps with 3d depth rendering in some browsers */
                            perspective: 800px; 
                        }
                        .loader {
                            position: relative;
                            width: 50px;
                            height: 50px;
                            transform-style: preserve-3d;
                            animation: spin 4s linear infinite;
                        }
                        .ring {
                            position: absolute;
                            inset: 0;
                            border-radius: 50%;
                            border-top: 5px solid #00e5ff;
                            border-left: 5px solid #00e5ff;
                            filter: drop-shadow(0 0 12px #00e5ff);
                        }
                        .ring:nth-child(1) {
                            transform: rotateX(70deg);
                        }
                        .ring:nth-child(2) {
                            transform: rotateX(70deg) rotateY(60deg);
                            border-top-color: #ff00d2;
                            border-left-color: #ff00d2;
                        }
                        .ring:nth-child(3) {
                            transform: rotateX(70deg) rotateY(-60deg);
                            border-top-color: #a6ff00;
                            border-left-color: #a6ff00;
                        }
                        @keyframes spin {
                            from { transform: rotateX(0deg) rotateY(0deg); }
                            to { transform: rotateX(360deg) rotateY(360deg); }
                        }
                    `}
                </style>
                
                <div className="loader-container">
                    <div className="loader">
                        <div className="ring"></div>
                        <div className="ring"></div>
                        <div className="ring"></div>
                    </div>
                </div>

                <h3 style={{ margin: "0 0 8px 0", color: "#0f172a", fontSize: "1.2rem" }}>{slide.title}</h3>
                <p style={{ margin: 0, fontSize: "0.95rem", fontWeight: "500" }}>Drafting Text & Fact-Checking...</p>
            </div>
        );
    }

    let processedCode = rawCode;
    if (!isFinished && slide.status !== "PREVIEW_READY" && slide.status !== "APPROVED") {
        processedCode = processedCode.replace(/src="\/api\/v1/g, `src="${API_BASE_URL}/api/v1`);
        processedCode = processedCode.replace(/src="(?:http:\/\/localhost:8080)?\/api\/v1\/images\/generate\?prompt=([^"]+)"/g, (match, p1) => {
            const cleanPrompt = decodeURIComponent(p1).split('&')[0].replace(/\+/g, ' ').trim();
            if (!cleanPrompt || cleanPrompt === "[DESCRIPTIVE_PROMPT]") return match;
            const secureUrl = `${API_BASE_URL}/api/v1/images/generate?prompt=${encodeURIComponent(cleanPrompt)}`;
            return `src="${secureUrl}"`;
        });
        processedCode = processedCode.replace(/<img /g, '<img loading="lazy" onerror="this.onerror=null;this.src=\'https://picsum.photos/seed/fallback/800/450\';" ');
    }

    const iframeSrcDoc = `
        <!DOCTYPE html>
        <html>
        <head>
            <script src="https://cdn.tailwindcss.com"></script>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800;900&display=swap" rel="stylesheet">
            <style>
                body, html { margin: 0; padding: 0; width: 100%; height: 100%; background: #f1f5f9; font-family: 'Inter', sans-serif; overflow: hidden; }
                .viewport-wrapper { position: absolute; top: 0; left: 0; width: 100vw; height: 100vh; }
                .slide-scaler { position: absolute; width: 1920px; height: 1080px; box-sizing: border-box; background: white; top: 50%; left: 50%; margin-top: -540px; margin-left: -960px; transform: scale(0.40); transform-origin: center center; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4); overflow: hidden; }
                .slide-scaler > div { width: 100% !important; height: 100% !important; box-sizing: border-box; }
            </style>
        </head>
        <body>
            <div class="viewport-wrapper">
                <div class="slide-scaler">
                    ${processedCode}
                </div>
            </div>
        </body>
        </html>
    `;

    const codeLines = processedCode.split('\n');

    return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", background: "white", marginBottom: "20px", overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", justifycontent: "space-between", justifyContent: "space-between", padding: "12px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <div style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: "10px", color: "#0f172a" }}>
                    {slide.title}
                    {isGenerating && <span style={{ color: "#3b82f6", fontSize: "0.85rem", animation: "pulse 1.5s infinite" }}>● Designing Layout...</span>}
                </div>
                <div style={{ display: "flex", gap: "4px" }}>
                    <button
                        onClick={() => setViewMode("preview")}
                        style={{ padding: "6px 12px", background: viewMode === "preview" ? "#e2e8f0" : "transparent", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", color: "#0f172a" }}
                    >
                        Preview
                    </button>
                    <button
                        onClick={() => setViewMode("code")}
                        style={{ padding: "6px 12px", background: viewMode === "code" ? "#e2e8f0" : "transparent", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", color: "#0f172a" }}
                    >
                        Code
                    </button>
                </div>
            </div>

            <div style={{ height: "460px", width: "100%", background: "#e2e8f0", position: "relative", overflow: "hidden" }}>
                {viewMode === "preview" ? (
                    <iframe
                        title={`slide-${slide.slide_id}`}
                        srcDoc={iframeSrcDoc}
                        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                        sandbox="allow-scripts allow-same-origin"
                    />
                ) : (
                    <div style={{ position: "relative", height: "100%", background: "#1e1e1e", display: "flex", flexDirection: "column" }}>
                        {/* Copy Code Bar */}
                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 16px", background: "#2d2d2d", borderBottom: "1px solid #3c3c3c" }}>
                            <button
                                onClick={handleCopy}
                                style={{ background: copied ? "#22c55e" : "#3c3c3c", color: "white", border: "none", padding: "4px 12px", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600", transition: "background 0.2s" }}
                            >
                                {copied ? "✓ Copied!" : "Copy Code"}
                            </button>
                        </div>

                        {/* Integrated IDE Core Area */}
                        <div style={{ display: "flex", flex: 1, overflowY: "auto", fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: "13px", lineHeight: "1.6" }}>
                            {/* Line Number Gutter */}
                            <div style={{ padding: "16px 12px", textAlign: "right", color: "#858585", background: "#1e1e1e", borderRight: "1px solid #2d2d2d", selectLight: "none", userSelect: "none", minWidth: "30px" }}>
                                {codeLines.map((_, i) => (
                                    <div key={i}>{i + 1}</div>
                                ))}
                            </div>
                            {/* Code Payload Content Area */}
                            <div style={{ padding: "16px", color: "#d4d4d4", whiteSpace: "pre", flex: 1 }}>
                                <code dangerouslySetInnerHTML={{ __html: highlightSyntax(processedCode) }} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveSlideCard;